const manualStores = {
    filename: 'manual_store_upload_template.csv',
    summary: 'Accepted: .csv or .xlsx.',
    columns: [
        {name: 'store_name', type: 'text', required: true, notes: "Used to compute the store's unique id"},
        {name: 'banner_name', type: 'text', required: true, notes: "Used to compute the store's unique id"},
        {name: 'chain_name', type: 'text', required: true, notes: 'Stored internally as chain'},
        {name: 'address', type: 'text', required: true, notes: 'Commas are stripped; used to compute the unique id'},
        {name: 'city', type: 'text', required: true, notes: 'Commas are stripped'},
        {name: 'state', type: 'text', required: true, notes: 'Upper-cased and de-spaced automatically (nh becomes NH)'},
        {name: 'open_date', type: 'text', required: false, notes: 'YYYY-MM-DD. If blank, defaults to an auto-computed date 3 months out'},
        {name: 'closed_date', type: 'text', required: false, notes: 'YYYY-MM-DD. Leave blank if the store is not closed'},
    ],
    exampleRows: [
        ["Joe's Market", "Joe's Market", "Joe's Holdings", '123 Main St', 'Pittsfield', 'MA', 'indie', '2026-01-01', ''],
        ['Green Grocer', 'Green Grocer', 'Greens Inc', '456 Oak Ave', 'Albany', 'NY', 'chain', '', ''],
    ],
};

const universe = {
    filename: 'universe_upload_template.csv',
    summary: 'Accepted: .csv only. Use these exact column names. Build the id column from the store_universe QC extract (source::version::source_store_id).',
    columns: [
        {name: 'canonical_store_id', type: 'text', required: true, notes: 'Canonical store id from the projections database: version::uid (e.g. 2026-P03::01M35J67YHTKSMBKTMEEQVD07Y)'},
        {name: 'store_name', type: 'text', required: true, notes: ''},
        {name: 'banner_name', type: 'text', required: true, notes: ''},
        {name: 'chain_name', type: 'text', required: true, notes: 'May be blank'},
        {name: 'address', type: 'text', required: true, notes: ''},
        {name: 'city', type: 'text', required: true, notes: ''},
        {name: 'state', type: 'text', required: true, notes: ''},
        {name: 'zip', type: 'text', required: true, notes: 'Column is named zip (not zip_code)'},
        {name: 'sample', type: 'integer', required: true, notes: '0 or 1 (1 = in sample)'},
        {name: 'classification', type: 'text', required: true, notes: 'Stored as provided'},
        {name: 'standard_region_cell_id', type: 'integer', required: true, notes: 'Blank skips the standard cell link for that row'},
        {name: 'census_region_cell_id', type: 'integer', required: true, notes: 'Blank skips the census cell link for that row'},
        {name: 'acv_2023', type: 'number', required: false, notes: 'One acv_YYYY column per year; at least one is required; add/remove year columns as needed; blank cells allowed'},
        {name: 'acv_2024', type: 'number', required: false, notes: 'ACV for the year'},
        {name: 'acv_2025', type: 'number', required: false, notes: 'ACV for the year'},
    ],
    exampleRows: [
        ['2026-P03::01M35J67YHTKSMBKTMEEQVD07Y', 'Adams Fairacre Farms 1', 'Adams Fairacre Farms 1', '', '1560 Ulster Ave', 'Lake Katrine', 'NY', '12449', '0', 'chain', '999', '994', '16.51', '17.19', '17.90'],
        ['2026-P03::01M35J3EQV2CQZVT85ZP2Z61K1', 'Whole Foods - Midtown', 'Whole Foods', 'Whole Foods Market', '789 5th Ave', 'New York', 'NY', '10022', '1', 'chain', '111', '222', '40.10', '41.30', '42.05'],
    ],
};

const cellTargets = {
    filename: 'cell_targets_template.csv',
    summary: 'Accepted: .csv only. Use these exact column names.',
    columns: [
        {name: 'year', type: 'integer', required: true, notes: 'Projection year (e.g. 2024)'},
        {name: 'cell_id', type: 'integer', required: true, notes: 'Cell identifier'},
        {name: 'store_count', type: 'integer', required: true, notes: 'Number of stores in the universe for the cell'},
        {name: 'acv', type: 'number', required: true, notes: 'Total ACV for the cell'},
        {name: 'acv_adjusted', type: 'number', required: true, notes: 'Adjusted total ACV used in projection calculations'},
    ],
    exampleRows: [
        ['2024', '93', '10', '1000.0', '1000.0'],
        ['2024', '11', '8', '800.0', '800.0'],
    ],
};

const csg = {
    summary:
        'Accepted: .xlsx (single sheet). Filename must include a quarter designator, e.g. 2025q3. ' +
        'If reloading files for a version, they must be reloaded in chronological order by quarter.',
    columns: [
        {name: 'Company Name', type: 'text', required: true, notes: 'Used as the banner name'},
        {name: 'Parent Name', type: 'text', required: true, notes: 'Used to classify the store as chain'},
        {name: 'Company ID', type: 'text', required: true, notes: 'Used as the unique store id'},
        {name: 'Address 1', type: 'text', required: true, notes: 'Store street address'},
        {name: 'Zip', type: 'text', required: true, notes: 'A trailing -#### suffix is stripped automatically'},
        {name: 'State', type: 'text', required: true, notes: 'Automatically upper-cased and stripped of spaces'},
        {name: 'Store No', type: 'text', required: false, notes: 'Store number'},
        {name: 'Store Weekly Volume', type: 'number', required: false, notes: ''},
        {name: 'Total Sales', type: 'number', required: false, notes: ''},
        {name: 'FIPS', type: 'text', required: false, notes: ''},
        {
            name: 'Amenity columns (24 Hour Store, Pharmacy, Wi-Fi, Wine, Tobacco, Bulk Foods, etc.)',
            type: 'boolean',
            required: false,
            notes: 'Any non-blank value in the cell marks the amenity as true',
        },
    ],
};

const cAndS = {
    summary: 'Accepted: .xlsx with "Master List" and "Allegiance & Associated Grocers" sheets. '
        + 'Filename must include a date, e.g. C&S Master List_2.6.25.xlsx.',
    columns: [
        {name: 'OBIEE Store #', type: 'text', required: true, notes: 'Used as the unique store id'},
        {name: 'Store Front Identifier', type: 'text', required: true, notes: 'Used as the banner name'},
        {name: 'Address', type: 'text', required: true, notes: ''},
        {name: 'City', type: 'text', required: true, notes: ''},
        {name: 'State', type: 'text', required: true, notes: ''},
        {name: 'Zip', type: 'text', required: true, notes: ''},
        {name: 'Estimated Total Sales', type: 'number', required: true, notes: 'Used as the ACV'},
    ],
};

const infra = {
    summary: 'Accepted: .xlsx (single sheet). Filename must include a period, e.g. INFRA Restatement List_P12 2025_v1.xlsx.',
    columns: [
        {name: 'Account Name', type: 'text', required: true, notes: 'Used as the banner name'},
        {name: 'Parent Account', type: 'text', required: true, notes: 'Used to classify the store as chain'},
        {name: 'Account Number', type: 'text', required: true, notes: 'Used as the unique store id'},
        {name: 'Mailing Street 1', type: 'text', required: true, notes: 'Commas are stripped automatically'},
        {name: 'Mailing City', type: 'text', required: true, notes: ''},
        {name: 'Mailing State', type: 'text', required: true, notes: ''},
        {name: 'Mailing Zip', type: 'text', required: true, notes: ''},
        {
            name: 'Location Type', type: 'text', required: true,
            notes: 'Only Main Storefront and Storefront rows are kept; all other rows are dropped',
        },
        {name: 'Annual Sales', type: 'number', required: false, notes: 'Divided by 1,000,000 to compute the ACV'},
        {
            name: 'Open Date', type: 'date', required: false,
            notes: 'A real date (e.g. 10/2/2025); non-date text (e.g. "non-retail location") is treated as blank',
        },
    ],
};

const cellToMarkets = {
    filename: 'cell_to_markets_template.csv',
    summary: 'Accepted: .csv only. Use these exact column names.',
    columns: [
        {name: 'universe', type: 'text', required: true, notes: ''},
        {name: 'region', type: 'text', required: true, notes: ''},
        {name: 'geographic_region', type: 'text', required: true, notes: ''},
        {name: 'market_tag', type: 'text', required: true, notes: ''},
        {name: 'Nucleus Market ID', type: 'integer', required: true, notes: ''},
        {name: 'Market', type: 'text', required: true, notes: ''},
        {name: 'cell_id', type: 'integer', required: true, notes: ''},
        {name: 'cell description', type: 'text', required: false, notes: ''},
    ],
    exampleRows: [
        ['natural enhanced', 'Northeast', 'New England', 'core', '101', 'Boston', '93', 'Boston core market'],
        ['infra', 'West', 'Pacific', 'expansion', '102', 'Los Angeles', '11', 'LA expansion market'],
    ],
};

const canonicalStoreOverrides = {
    filename: 'canonical_store_override_template.csv',
    summary: 'Accepted: .csv only. Use these exact column names. Blank or NULL values are ignored and leave the existing value unchanged. ' +
        'A bulk upload cannot remove an override; use the single store edit on the Store tab to remove one. ' +
        'Dates must be YYYY-MM-DD: if you edit the file in Excel, set the date columns to Text or the yyyy-mm-dd format before saving, or Excel will change the dates.',
    columns: [
        {name: 'canonical_store_id', type: 'text', required: true, notes: 'Canonical store id from the projections database: version::uid (e.g. 2026-P03::01M35J3T0N635VV8TW2DV7R2HJ). The version must match the selected version'},
        {name: 'open_date', type: 'text', required: false, notes: 'YYYY-MM-DD. Must be before closed_date. Blank or NULL means no change'},
        {name: 'closed_date', type: 'text', required: false, notes: 'YYYY-MM-DD. Blank or NULL means no change'},
        {name: 'classification', type: 'text', required: false, notes: 'indie or chain (any case). Blank or NULL means no change'},
    ],
    exampleRows: [
        ['2026-P03::01M35J3T0N635VV8TW2DV7R2HJ', '2019-12-31', '2025-03-31', 'chain'],
        ['2026-P03::01M35J3T0NXE50B034X3J1QX32', '', '2025-06-30', ''],
        ['2026-P03::01M35J3T0NEHER0V7JN08NVRYT', '2020-01-15', 'NULL', 'NULL'],
        ['2026-P03::01M35J3T0N2YQ4ZK8W6D3R5T7M', '', '', 'indie'],
    ],
};

export const uploadTemplates = {
    manual: manualStores,
    universe,
    cellTargets,
    cellToMarkets,
    canonicalStoreOverrides,
    csg,
    c_and_s: cAndS,
    infra,
};
